import { Component, HostBinding, Input } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faSpinner, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { ListActionButtonSpec } from './list-actions-cell.component';
import { ListActionsCellComponent } from './list-actions-cell.component';
import { MiddleTruncateComponent } from './middle-truncate.component';

/**
 * A list-table cell with a text cut by an ellipsis and the row's quick actions as icons at its right end. The icons
 * show while the row is hovered or the cell has focus, and the text then ends in an ellipsis before them. With
 * `reserve`, or on a screen with no hover, they keep their own space after the text.
 */
@Component({
    selector: 'app-hover-actions-cell',
    standalone: true,
    imports: [FontAwesomeModule, MiddleTruncateComponent],
    template: `
        <span class="lt-hover-value">
            <span class="lt-hover-line">
            @if (html) {
                <span class="lt-hover-text lt-hover-html" [innerHTML]="html"></span>
            } @else if (!text) {
                <span class="lt-hover-text lt-hover-text--muted">—</span>
            } @else if (keepFrom) {
                <app-middle-truncate class="lt-hover-middle" [value]="text" [keepFrom]="keepFrom"></app-middle-truncate>
            } @else {
                <span class="lt-hover-text" [title]="text">{{ text }}</span>
            }
            @if (badge) {
                <span
                    class="nf-badge nf-badge--{{ badge.tone || 'muted' }} lt-hover-badge"
                    [attr.id]="badge.id || null"
                    [attr.title]="badge.title || null"
                    >{{ badge.label }}</span
                >
            }
            </span>
            @if (subtext) {
                <span class="lt-hover-sub" [title]="subtext">{{ subtext }}</span>
            }
        </span>
        @if (buttons.length) {
            <span class="lt-hover-actions">
                @for (button of buttons; track button.action) {
                    @if (button.href) {
                        <a
                            class="lt-hover-action"
                            [id]="button.id || cellId + '_' + button.action"
                            [href]="button.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            [title]="button.label"
                            [attr.aria-label]="button.ariaLabel || button.label"
                            (click)="$event.stopPropagation()"
                        >
                            <fa-icon [icon]="faIconOf(button) || faUpRightFromSquare" aria-hidden="true"></fa-icon>
                        </a>
                    } @else {
                        <button
                            type="button"
                            class="lt-hover-action"
                            [class.lt-hover-action--danger]="!!button.danger"
                            [id]="button.id || cellId + '_' + button.action"
                            [disabled]="isBusy(button) || isDisabled(button)"
                            [title]="titleOf(button)"
                            [attr.aria-label]="button.ariaLabel || button.label"
                            (click)="run(button.action); $event.stopPropagation()"
                        >
                            @if (faIconOf(button); as icon) {
                                <fa-icon
                                    [icon]="isBusy(button) ? faSpinner : icon"
                                    [animation]="isBusy(button) ? 'spin' : undefined"
                                    aria-hidden="true"
                                ></fa-icon>
                            } @else {
                                {{ isBusy(button) ? button.busyTitle || 'Working…' : button.label }}
                            }
                        </button>
                    }
                }
            </span>
        }
    `,
    styles: [
        `
            :host {
                align-items: center;
                display: flex;
                min-width: 0;
                position: relative;
                width: 100%;
            }

            /* The text line, with the subtext below it. Both give way to an ellipsis before the icons when they show. */
            .lt-hover-value {
                display: flex;
                flex: 1 1 auto;
                flex-direction: column;
                justify-content: center;
                min-width: 0;
            }

            /* The text gives way to the ellipsis before the badge does. */
            .lt-hover-line {
                align-items: center;
                display: flex;
                gap: 0.375rem;
                min-width: 0;
            }

            .lt-hover-sub {
                color: var(--subText);
                font-size: 0.75rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .lt-hover-text {
                display: block;
                flex: 0 1 auto;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .lt-hover-badge {
                flex: 0 0 auto;
            }

            /* Out of the flow at rest, so the text has the full width, yet still reachable by Tab. On hover or focus
               the icons join the flow, and the text shrinks to an ellipsis before them. */
            .lt-hover-actions {
                align-items: center;
                display: inline-flex;
                flex: 0 0 auto;
                gap: 0.125rem;
                opacity: 0;
                padding-left: 0.875rem;
                pointer-events: none;
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
                transition: opacity 0.12s;
            }

            :host-context(.lt-row:hover) .lt-hover-actions,
            :host(:focus-within) .lt-hover-actions {
                opacity: 1;
                pointer-events: auto;
                position: static;
                transform: none;
            }

            .lt-hover-action {
                align-items: center;
                background-color: var(--surface-raised, #fff);
                border: 1px solid color-mix(in srgb, var(--tableText) 24%, transparent);
                border-radius: var(--lt-radius, 0.25rem);
                box-sizing: border-box;
                cursor: pointer;
                display: inline-flex;
                font-size: 0.75rem;
                height: 1.75rem;
                justify-content: center;
                min-width: 1.75rem;
                padding: 0 0.25rem;
            }

            /* The colours sit on the icon too, because the global link style forces its own colour on an <a>. */
            .lt-hover-action,
            .lt-hover-action fa-icon {
                color: var(--lt-icon, var(--icon));
            }

            .lt-hover-action fa-icon {
                font-size: 0.875rem;
                line-height: 1;
            }

            .lt-hover-action:hover:not(:disabled) {
                background-color: var(--surface-sunken);
            }

            .lt-hover-action:hover:not(:disabled),
            .lt-hover-action:hover:not(:disabled) fa-icon {
                color: var(--lt-icon-active, var(--primary));
            }

            .lt-hover-action--danger:hover:not(:disabled),
            .lt-hover-action--danger:hover:not(:disabled) fa-icon {
                color: var(--red);
            }

            .lt-hover-action:focus-visible {
                outline: 2px solid var(--primaryColor);
                outline-offset: 1px;
            }

            .lt-hover-action:disabled,
            .lt-hover-action:disabled fa-icon {
                color: var(--placeholder);
                cursor: default;
            }

            .lt-hover-text--muted {
                color: var(--placeholder);
            }

            .lt-hover-middle {
                flex: 0 1 auto;
                min-width: 0;
            }

            /* Reserved: the icons hold their slot right after the text at all times, so the text never reflows. */
            :host(.lt-hover--reserve) .lt-hover-value {
                flex: 0 1 auto;
            }

            :host(.lt-hover--reserve) .lt-hover-actions {
                padding-left: 0.25rem;
                position: static;
                transform: none;
            }

            /* No hover to reveal them, so the icons take their own space after the text. */
            @media (hover: none) {
                .lt-hover-actions {
                    opacity: 1;
                    pointer-events: auto;
                    position: static;
                    transform: none;
                }
            }
        `,
    ],
})
export class HoverActionsCellComponent extends ListActionsCellComponent {
    /** The cell's text, such as the row's name. The `title` keeps it whole where the ellipsis cuts it. */
    @Input() text = '';
    /** Markup in place of `text`, for a cell that draws flags or extra lines. The page must escape the values in it. */
    @Input() html = '';
    /** A second line under the text, such as an id. It is cut by an ellipsis too. */
    @Input() subtext = '';
    /** Keeps the text from the last `keepFrom` on in view, such as ':' for a port. See MiddleTruncateComponent. */
    @Input() keepFrom = '';
    /** Keeps the icons' space after the text at all times, for a value whose end must never be covered. */
    @HostBinding('class.lt-hover--reserve') @Input() reserve = false;
    /** A badge after the text, such as "Default", in the global nf-badge style. */
    @Input() badge: HoverCellBadge | null = null;

    readonly faSpinner = faSpinner;
    readonly faUpRightFromSquare = faUpRightFromSquare;

    /** The button's Font Awesome icon. A PrimeIcons class is for the Actions column, so the button shows its label instead. */
    faIconOf(button: ListActionButtonSpec): IconDefinition | null {
        return typeof button.icon === 'object' ? button.icon : null;
    }
}

/** A badge the hover-actions cell draws after its text. `tone` picks the nf-badge modifier and defaults to muted. */
export interface HoverCellBadge {
    label: string;
    id?: string;
    title?: string;
    tone?: string;
}
